import type { CharacterLook } from '../game/types';
import { drawPixelText } from './pixelFont';
import { mixColor, noise, px, spriteFromDraw, type Sprite } from './sprite';

/*
 * Procedural pixel people. Every customer is assembled from a CharacterLook
 * (skin, hair, hat, eyes, shirt, accessory) into cached sprites for each pose.
 */

export type Mood = 'normal' | 'talk' | 'happy' | 'angry' | 'blink' | 'eat' | 'sleep';

export const SEATED_W = 20;
export const SEATED_H = 30;
export const MINI_W = 12;
export const MINI_H = 18;
export const WALKER_W = 12;
export const WALKER_H = 24;

const HEAD_ROWS = [
  '..######..',
  '.########.',
  '##########',
  '##########',
  '##########',
  '##########',
  '##########',
  '##########',
  '.########.',
  '..######..',
];

const shade = (c: string, k: number): string => mixColor(c, '#000000', k);
const light = (c: string, k: number): string => mixColor(c, '#ffffff', k);

function lookKey(look: CharacterLook): string {
  return [
    look.species,
    look.skin,
    look.hair,
    look.hairStyle,
    look.hat,
    look.hatColor,
    look.eyes,
    look.facialHair,
    look.shirt,
    look.shirtAlt,
    look.shirtStyle,
    look.accessory,
  ].join('|');
}

/* ───────────────────────────── seated at the counter ───────────────────────────── */

export function seatedSprite(look: CharacterLook, mood: Mood): Sprite {
  return spriteFromDraw(`seated:${lookKey(look)}:${mood}`, SEATED_W, SEATED_H, (ctx) => {
    if (look.species === 'raccoon') return drawRaccoon(ctx, mood);
    if (look.species === 'host') return drawHost(ctx);
    drawSeatedHuman(ctx, look, mood);
  });
}

function drawSeatedHuman(ctx: CanvasRenderingContext2D, look: CharacterLook, mood: Mood): void {
  const hx = 5;
  const hy = 4;
  // Hair that sits behind the head/shoulders first.
  drawHairBack(ctx, look, hx, hy);
  drawTorso(ctx, look, 1, 15, 18, 15);
  // Neck
  px(ctx, hx + 3, hy + 9, 4, 3, shade(look.skin, 0.12));
  drawHead(ctx, look, hx, hy, mood);
  drawHairFront(ctx, look, hx, hy);
  drawHat(ctx, look, hx, hy);
  drawAccessoryFront(ctx, look, hx, hy, 1, 15, 18);
}

function drawHead(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  hx: number,
  hy: number,
  mood: Mood,
): void {
  const skin = mood === 'angry' ? mixColor(look.skin, '#e0413a', 0.35) : look.skin;
  const skinShade = shade(skin, 0.14);
  HEAD_ROWS.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] !== '#') continue;
      const edgeRight = x >= 8 || (y >= 8 && x >= 6);
      px(ctx, hx + x, hy + y, 1, 1, edgeRight ? skinShade : skin);
    }
  });
  // Ears
  px(ctx, hx - 1, hy + 4, 1, 2, skinShade);
  px(ctx, hx + 10, hy + 4, 1, 2, skinShade);
  // Cheeks
  if (mood === 'happy') {
    px(ctx, hx + 1, hy + 6, 1, 1, mixColor(skin, '#ff6b6b', 0.45));
    px(ctx, hx + 8, hy + 6, 1, 1, mixColor(skin, '#ff6b6b', 0.45));
  }
  drawEyes(ctx, look, hx, hy, mood);
  drawMouth(ctx, look, hx, hy, mood);
  drawFacialHair(ctx, look, hx, hy, mood);
}

function drawEyes(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  hx: number,
  hy: number,
  mood: Mood,
): void {
  const eye = '#1a1410';
  const ex1 = hx + 2;
  const ex2 = hx + 7;
  const ey = hy + 4;
  switch (look.eyes) {
    case 'sunglasses':
      px(ctx, hx + 1, ey, 8, 1, '#0d0d12');
      px(ctx, hx + 1, ey + 1, 3, 1, '#0d0d12');
      px(ctx, hx + 6, ey + 1, 3, 1, '#0d0d12');
      px(ctx, hx + 2, ey, 1, 1, '#6d8bd1');
      px(ctx, hx + 7, ey, 1, 1, '#6d8bd1');
      return;
    case 'glasses':
      px(ctx, hx + 1, ey - 1, 3, 1, '#2d2d33');
      px(ctx, hx + 6, ey - 1, 3, 1, '#2d2d33');
      px(ctx, hx + 4, ey, 2, 1, '#2d2d33');
      px(ctx, hx + 1, ey + 2, 3, 1, '#2d2d33');
      px(ctx, hx + 6, ey + 2, 3, 1, '#2d2d33');
      break;
    case 'glow':
      px(ctx, ex1, ey, 1, 2, '#ffe14d');
      px(ctx, ex2, ey, 1, 2, '#ffe14d');
      return;
    default:
      break;
  }
  if (mood === 'blink' || mood === 'sleep' || look.eyes === 'sleepy') {
    px(ctx, ex1 - 1, ey + 1, 2, 1, eye);
    px(ctx, ex2, ey + 1, 2, 1, eye);
    if (look.eyes === 'sleepy') {
      px(ctx, ex1 - 1, ey + 2, 2, 1, shade(look.skin, 0.25));
      px(ctx, ex2, ey + 2, 2, 1, shade(look.skin, 0.25));
    }
    return;
  }
  if (mood === 'happy') {
    px(ctx, ex1 - 1, ey + 1, 1, 1, eye);
    px(ctx, ex1, ey, 1, 1, eye);
    px(ctx, ex1 + 1, ey + 1, 1, 1, eye);
    px(ctx, ex2 - 1, ey + 1, 1, 1, eye);
    px(ctx, ex2, ey, 1, 1, eye);
    px(ctx, ex2 + 1, ey + 1, 1, 1, eye);
    return;
  }
  px(ctx, ex1, ey, 1, 2, eye);
  px(ctx, ex2, ey, 1, 2, eye);
  if (mood === 'angry') {
    px(ctx, ex1 - 1, ey - 2, 2, 1, eye);
    px(ctx, ex1 + 1, ey - 1, 1, 1, eye);
    px(ctx, ex2, ey - 2, 2, 1, eye);
    px(ctx, ex2 - 1, ey - 1, 1, 1, eye);
  }
}

function drawMouth(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  hx: number,
  hy: number,
  mood: Mood,
): void {
  const lip = shade(look.skin, 0.45);
  const my = hy + 7;
  switch (mood) {
    case 'talk':
    case 'eat':
      px(ctx, hx + 4, my, 2, 2, '#3a1512');
      px(ctx, hx + 4, my + 1, 2, 1, '#b8454a');
      break;
    case 'happy':
      px(ctx, hx + 3, my, 1, 1, lip);
      px(ctx, hx + 4, my + 1, 2, 1, lip);
      px(ctx, hx + 6, my, 1, 1, lip);
      break;
    case 'angry':
      px(ctx, hx + 3, my + 1, 1, 1, lip);
      px(ctx, hx + 4, my, 2, 1, lip);
      px(ctx, hx + 6, my + 1, 1, 1, lip);
      break;
    case 'sleep':
      px(ctx, hx + 4, my, 2, 1, lip);
      break;
    default:
      px(ctx, hx + 4, my, 2, 1, lip);
  }
}

function drawFacialHair(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  hx: number,
  hy: number,
  mood: Mood,
): void {
  const c = look.hair === '#d8d8d8' || look.hair === '#8f8f8f' ? look.hair : shade(look.hair, 0.05);
  switch (look.facialHair) {
    case 'beard':
      px(ctx, hx, hy + 5, 1, 3, c);
      px(ctx, hx + 9, hy + 5, 1, 3, c);
      px(ctx, hx + 1, hy + 7, 2, 2, c);
      px(ctx, hx + 7, hy + 7, 2, 2, c);
      px(ctx, hx + 2, hy + 9, 6, 1, c);
      px(ctx, hx + 3, hy + 10, 4, 1, c);
      px(ctx, hx + 3, hy + 6, 4, 1, c);
      if (mood !== 'talk' && mood !== 'eat') px(ctx, hx + 3, hy + 8, 4, 1, c);
      break;
    case 'mustache':
      px(ctx, hx + 3, hy + 6, 4, 1, c);
      px(ctx, hx + 2, hy + 7, 1, 1, c);
      px(ctx, hx + 7, hy + 7, 1, 1, c);
      break;
    case 'stubble':
      for (let y = 6; y < 10; y++)
        for (let x = 1; x < 9; x++)
          if (noise(x, y, 7) > 0.62) px(ctx, hx + x, hy + y, 1, 1, shade(look.skin, 0.3));
      break;
    default:
      break;
  }
}

function drawHairBack(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  hx: number,
  hy: number,
): void {
  const c = look.hair;
  switch (look.hairStyle) {
    case 'long':
      px(ctx, hx - 1, hy + 1, 12, 13, shade(c, 0.1));
      px(ctx, hx - 2, hy + 8, 2, 7, shade(c, 0.1));
      px(ctx, hx + 10, hy + 8, 2, 7, shade(c, 0.1));
      break;
    case 'afro':
      for (let y = -4; y <= 8; y++)
        for (let x = -4; x <= 13; x++) {
          const dx = x - 4.5;
          const dy = y - 2;
          if (dx * dx + dy * dy * 1.2 < 62)
            px(ctx, hx + x, hy + y, 1, 1, noise(x, y, 3) > 0.8 ? light(c, 0.12) : c);
        }
      break;
    case 'mullet':
      px(ctx, hx - 1, hy + 3, 12, 11, shade(c, 0.08));
      break;
    case 'ponytail':
      px(ctx, hx + 9, hy + 3, 3, 8, shade(c, 0.1));
      px(ctx, hx + 10, hy + 11, 2, 3, shade(c, 0.1));
      break;
    default:
      break;
  }
}

function drawHairFront(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  hx: number,
  hy: number,
): void {
  if (look.hat === 'hood' || look.hat === 'cowboy' || look.hat === 'church') {
    // Hats that cover the crown still show a little fringe.
    if (look.hat !== 'hood' && look.hairStyle !== 'bald') {
      px(ctx, hx, hy + 2, 1, 3, look.hair);
      px(ctx, hx + 9, hy + 2, 1, 3, look.hair);
    }
    return;
  }
  const c = look.hair;
  const hi = light(c, 0.18);
  switch (look.hairStyle) {
    case 'bald':
      px(ctx, hx + 2, hy + 1, 2, 1, light(look.skin, 0.35));
      return;
    case 'buzz':
      px(ctx, hx + 2, hy, 6, 1, shade(c, 0.1));
      px(ctx, hx + 1, hy + 1, 8, 1, shade(c, 0.1));
      return;
    case 'mohawk':
      px(ctx, hx + 4, hy - 3, 2, 5, c);
      px(ctx, hx + 4, hy - 3, 1, 1, hi);
      return;
    case 'afro':
      return;
    case 'curly':
      for (let x = 0; x < 10; x++) px(ctx, hx + x, hy - 1 + ((x % 2) ^ 1), 1, 1, c);
      px(ctx, hx, hy, 10, 2, c);
      px(ctx, hx, hy + 2, 2, 2, c);
      px(ctx, hx + 8, hy + 2, 2, 2, c);
      px(ctx, hx + 3, hy, 2, 1, hi);
      return;
    default: {
      // short / long / bun / ponytail / mullet share a cap of hair
      px(ctx, hx + 1, hy - 1, 8, 1, c);
      px(ctx, hx, hy, 10, 2, c);
      px(ctx, hx, hy + 2, 1, 3, c);
      px(ctx, hx + 9, hy + 2, 1, 3, c);
      px(ctx, hx + 1, hy + 2, 3, 1, c);
      px(ctx, hx + 2, hy, 3, 1, hi);
      if (look.hairStyle === 'bun') {
        px(ctx, hx + 3, hy - 4, 4, 3, c);
        px(ctx, hx + 4, hy - 4, 2, 1, hi);
      }
    }
  }
}

function drawHat(ctx: CanvasRenderingContext2D, look: CharacterLook, hx: number, hy: number): void {
  const c = look.hatColor;
  const dark = shade(c, 0.25);
  switch (look.hat) {
    case 'trucker':
      px(ctx, hx, hy - 2, 10, 4, c);
      px(ctx, hx + 2, hy - 2, 6, 3, '#f2efe6');
      px(ctx, hx + 4, hy - 1, 2, 1, dark);
      px(ctx, hx - 1, hy + 2, 12, 1, dark);
      break;
    case 'cap':
      px(ctx, hx, hy - 2, 10, 4, c);
      px(ctx, hx + 2, hy - 2, 3, 1, light(c, 0.2));
      px(ctx, hx - 1, hy + 2, 12, 1, dark);
      break;
    case 'cowboy':
      px(ctx, hx + 2, hy - 4, 6, 5, c);
      px(ctx, hx + 2, hy - 1, 6, 1, dark);
      px(ctx, hx - 3, hy + 1, 16, 2, c);
      px(ctx, hx - 3, hy + 2, 16, 1, dark);
      break;
    case 'church':
      px(ctx, hx - 1, hy - 3, 12, 4, c);
      px(ctx, hx - 4, hy + 1, 18, 2, c);
      px(ctx, hx - 4, hy + 2, 18, 1, dark);
      px(ctx, hx + 8, hy - 2, 2, 2, '#fff4f9');
      px(ctx, hx + 7, hy - 3, 1, 1, '#ffd23f');
      break;
    case 'beanie':
      px(ctx, hx, hy - 2, 10, 4, c);
      px(ctx, hx - 1, hy + 1, 12, 2, dark);
      px(ctx, hx + 4, hy - 4, 2, 2, light(c, 0.3));
      break;
    case 'tiara':
      px(ctx, hx + 2, hy - 2, 6, 1, '#ffd23f');
      px(ctx, hx + 2, hy - 3, 1, 1, '#ffd23f');
      px(ctx, hx + 7, hy - 3, 1, 1, '#ffd23f');
      px(ctx, hx + 4, hy - 4, 2, 2, '#ff5fa2');
      break;
    case 'hood':
      px(ctx, hx - 2, hy - 2, 14, 3, c);
      px(ctx, hx - 2, hy + 1, 2, 12, c);
      px(ctx, hx + 10, hy + 1, 2, 12, c);
      break;
    case 'headband':
      px(ctx, hx, hy + 1, 10, 2, c);
      px(ctx, hx + 1, hy + 1, 2, 1, '#ffffff');
      break;
    case 'visor':
      px(ctx, hx, hy, 10, 1, c);
      px(ctx, hx - 1, hy + 1, 12, 2, c);
      px(ctx, hx - 1, hy + 2, 12, 1, dark);
      break;
    default:
      break;
  }
}

function drawTorso(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const c = look.shirt;
  const alt = look.shirtAlt;
  const dark = shade(c, 0.22);
  // Shoulders rounded
  px(ctx, x + 2, y, w - 4, 1, c);
  px(ctx, x + 1, y + 1, w - 2, 1, c);
  px(ctx, x, y + 2, w, h - 2, c);
  // Arm shading
  px(ctx, x, y + 4, 2, h - 4, dark);
  px(ctx, x + w - 2, y + 4, 2, h - 4, dark);
  px(ctx, x + w - 4, y + 2, 2, h - 2, shade(c, 0.1));

  switch (look.shirtStyle) {
    case 'flannel':
      for (let yy = y + 1; yy < y + h; yy++)
        for (let xx = x + 1; xx < x + w - 1; xx++) {
          if ((xx - x) % 4 === 0 || (yy - y) % 4 === 0) px(ctx, xx, yy, 1, 1, shade(alt, 0.35));
        }
      break;
    case 'stripes':
      for (let yy = y + 2; yy < y + h; yy += 3) px(ctx, x + 1, yy, w - 2, 1, alt);
      break;
    case 'hawaiian':
      for (let i = 0; i < 16; i++) {
        const fx = x + 2 + Math.floor(noise(i, 1, 9) * (w - 4));
        const fy = y + 2 + Math.floor(noise(i, 2, 9) * (h - 3));
        px(ctx, fx, fy, 1, 1, i % 3 === 0 ? '#fff4c2' : i % 3 === 1 ? '#ff7eb6' : '#ffd23f');
      }
      break;
    case 'sparkle':
      for (let i = 0; i < 14; i++) {
        const fx = x + 1 + Math.floor(noise(i, 5, 4) * (w - 2));
        const fy = y + 1 + Math.floor(noise(i, 6, 4) * (h - 2));
        px(ctx, fx, fy, 1, 1, i % 2 ? '#ffffff' : '#ffe680');
      }
      break;
    case 'tank':
      px(ctx, x, y, 4, h, look.skin);
      px(ctx, x + w - 4, y, 4, h, look.skin);
      px(ctx, x, y + 4, 2, h - 4, shade(look.skin, 0.15));
      px(ctx, x + w - 2, y + 4, 2, h - 4, shade(look.skin, 0.15));
      px(ctx, x + 6, y, 6, 2, look.skin);
      break;
    case 'hoodie':
      px(ctx, x + 4, y, w - 8, 2, dark);
      px(ctx, x + 7, y + 2, 1, 4, '#f5f5f5');
      px(ctx, x + w - 8, y + 2, 1, 4, '#f5f5f5');
      px(ctx, x + 5, y + h - 5, w - 10, 3, dark);
      break;
    case 'scrubs':
      px(ctx, x + 7, y, 4, 3, look.skin);
      px(ctx, x + 8, y + 3, 2, 1, look.skin);
      px(ctx, x + 3, y + 6, 3, 3, dark);
      break;
    case 'suit':
      px(ctx, x + 6, y, 6, h, alt);
      px(ctx, x + 5, y, 2, 7, shade(c, 0.35));
      px(ctx, x + 11, y, 2, 7, shade(c, 0.35));
      px(ctx, x + 8, y + 4, 2, h - 4, shade(alt, 0.15));
      break;
    case 'turtleneck':
      px(ctx, x + 5, y - 2, 8, 3, c);
      px(ctx, x + 5, y - 2, 8, 1, light(c, 0.12));
      break;
    case 'polo':
      px(ctx, x + 6, y, 2, 2, alt);
      px(ctx, x + 10, y, 2, 2, alt);
      px(ctx, x + 8, y, 2, 4, shade(c, 0.2));
      px(ctx, x + 1, y + h - 3, w - 2, 1, alt);
      break;
    default:
      px(ctx, x + 7, y, 4, 2, look.skin);
      px(ctx, x + 8, y + 2, 2, 1, look.skin);
  }
}

function drawAccessoryFront(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  hx: number,
  hy: number,
  x: number,
  y: number,
  w: number,
): void {
  switch (look.accessory) {
    case 'sash':
      for (let i = 0; i < 14; i++)
        px(ctx, x + 2 + i, y + i, 3, 1, i % 5 === 0 ? '#ffd23f' : '#ffffff');
      break;
    case 'bowtie':
      px(ctx, x + 7, y + 1, 2, 3, '#111');
      px(ctx, x + 11, y + 1, 2, 3, '#111');
      px(ctx, x + 9, y + 2, 2, 1, '#333');
      break;
    case 'badge':
      px(ctx, x + 4, y + 4, 3, 3, '#f1c40f');
      px(ctx, x + 5, y + 3, 1, 1, '#f1c40f');
      px(ctx, x + 5, y + 5, 1, 1, '#b7950b');
      break;
    case 'phone':
      px(ctx, hx + 11, hy + 2, 5, 8, '#15151b');
      px(ctx, hx + 12, hy + 3, 3, 6, '#7fd6ff');
      px(ctx, hx + 12, hy + 3, 1, 1, '#ff3b3b');
      px(ctx, hx + 11, hy + 10, 4, 3, look.skin);
      break;
    case 'lanyard':
      px(ctx, x + 6, y, 1, 7, '#2e86de');
      px(ctx, x + 11, y, 1, 7, '#2e86de');
      px(ctx, x + 7, y + 7, 4, 4, '#ffffff');
      px(ctx, x + 8, y + 8, 2, 1, '#2e86de');
      break;
    case 'baby':
      px(ctx, x + 4, y + 5, 10, 9, '#6c5ce7');
      px(ctx, x + 6, y + 2, 6, 6, '#f6d3b3');
      px(ctx, x + 6, y + 1, 6, 2, '#74b9ff');
      px(ctx, x + 7, y + 5, 1, 1, '#333');
      px(ctx, x + 10, y + 5, 1, 1, '#333');
      break;
    case 'nametag':
      px(ctx, x + 3, y + 4, 5, 3, '#ffffff');
      px(ctx, x + 4, y + 5, 3, 1, '#c0392b');
      break;
    case 'camera':
      px(ctx, x + 5, y, 1, 6, '#111');
      px(ctx, x + 12, y, 1, 6, '#111');
      px(ctx, x + 5, y + 6, 8, 5, '#2d3436');
      px(ctx, x + 8, y + 7, 3, 3, '#636e72');
      px(ctx, x + 9, y + 8, 1, 1, '#a4d8ff');
      break;
    case 'mic':
      px(ctx, hx + 9, hy + 6, 3, 3, '#7f8c8d');
      px(ctx, hx + 10, hy + 9, 1, 5, '#111');
      px(ctx, hx + 9, hy + 12, 3, 3, look.skin);
      break;
    case 'femaJacket':
      drawPixelText(ctx, 'FEMA', x + 2, y + 5, '#ffd23f');
      break;
    case 'raincoat':
      px(ctx, x + 3, y - 1, w - 6, 2, '#ffd23f');
      px(ctx, x + 8, y + 1, 2, 14, shade(look.shirt, 0.3));
      px(ctx, x + 2, y + 9, 3, 1, '#ffd23f');
      px(ctx, x + 13, y + 9, 3, 1, '#ffd23f');
      break;
    case 'chain':
      for (let i = 0; i < 9; i++)
        px(ctx, x + 5 + i, y + 2 + Math.round(Math.sin((i / 8) * Math.PI) * 3), 1, 1, '#f5c542');
      px(ctx, x + 8, y + 5, 2, 2, '#f5c542');
      break;
    case 'suspenders':
      px(ctx, x + 4, y, 1, 15, '#8b1e1e');
      px(ctx, x + 13, y, 1, 15, '#8b1e1e');
      break;
    default:
      break;
  }
}

function drawRaccoon(ctx: CanvasRenderingContext2D, mood: Mood): void {
  const fur = '#8a8f98';
  const dark = '#4a4e57';
  const mask = '#1e2026';
  const oy = 9;
  // ears
  px(ctx, 5, oy, 3, 3, dark);
  px(ctx, 12, oy, 3, 3, dark);
  px(ctx, 6, oy + 1, 1, 1, '#d9a3a3');
  px(ctx, 13, oy + 1, 1, 1, '#d9a3a3');
  // head
  px(ctx, 4, oy + 2, 12, 8, fur);
  px(ctx, 5, oy + 10, 10, 1, fur);
  px(ctx, 4, oy + 5, 12, 2, mask);
  px(ctx, 9, oy + 5, 2, 1, fur);
  if (mood === 'blink') {
    px(ctx, 6, oy + 6, 2, 1, '#ffffff');
    px(ctx, 12, oy + 6, 2, 1, '#ffffff');
  } else {
    px(ctx, 6, oy + 5, 2, 2, '#ffffff');
    px(ctx, 12, oy + 5, 2, 2, '#ffffff');
    px(ctx, 7, oy + 5, 1, 1, '#000');
    px(ctx, 12, oy + 5, 1, 1, '#000');
  }
  px(ctx, 7, oy + 7, 6, 3, '#e8e3da');
  px(ctx, 9, oy + 7, 2, 1, '#111');
  if (mood === 'talk' || mood === 'eat') px(ctx, 9, oy + 9, 2, 1, '#6b2020');
  // body
  px(ctx, 4, oy + 11, 12, 10, fur);
  px(ctx, 7, oy + 12, 6, 8, '#b7bcc4');
  // paws on the counter
  px(ctx, 3, oy + 17, 3, 2, dark);
  px(ctx, 14, oy + 17, 3, 2, dark);
}

function drawHost(ctx: CanvasRenderingContext2D): void {
  const cloak = '#0b0b10';
  const edge = '#1c1c28';
  px(ctx, 3, 1, 14, 29, cloak);
  px(ctx, 2, 6, 16, 24, cloak);
  px(ctx, 4, 0, 12, 2, edge);
  // The face is a single glowing sign tile.
  px(ctx, 6, 4, 8, 9, '#ffd23f');
  px(ctx, 6, 4, 8, 1, '#fff2a8');
  px(ctx, 7, 6, 1, 4, '#111');
  px(ctx, 9, 8, 1, 2, '#111');
  px(ctx, 11, 6, 1, 4, '#111');
  px(ctx, 8, 9, 1, 1, '#111');
  px(ctx, 10, 9, 1, 1, '#111');
  px(ctx, 2, 14, 1, 16, edge);
  px(ctx, 17, 14, 1, 16, edge);
}

/* ───────────────────────────── booth minis & walkers ───────────────────────────── */

export function miniSprite(look: CharacterLook, mood: Mood): Sprite {
  return spriteFromDraw(`mini:${lookKey(look)}:${mood}`, MINI_W, MINI_H, (ctx) =>
    drawMini(ctx, look, mood, 0),
  );
}

function drawMini(
  ctx: CanvasRenderingContext2D,
  look: CharacterLook,
  mood: Mood,
  oy: number,
): void {
  if (look.species === 'raccoon') {
    px(ctx, 3, oy + 4, 6, 5, '#8a8f98');
    px(ctx, 3, oy + 6, 6, 1, '#1e2026');
    px(ctx, 2, oy + 9, 8, 7, '#8a8f98');
    return;
  }
  const hair = look.hair;
  // body
  px(ctx, 1, oy + 9, 10, 9, look.shirt);
  px(ctx, 1, oy + 11, 1, 7, shade(look.shirt, 0.25));
  px(ctx, 10, oy + 11, 1, 7, shade(look.shirt, 0.25));
  if (look.shirtStyle === 'stripes' || look.shirtStyle === 'flannel')
    for (let y = oy + 11; y < oy + 18; y += 2) px(ctx, 2, y, 8, 1, look.shirtAlt);
  if (look.shirtStyle === 'sparkle') {
    px(ctx, 3, oy + 11, 1, 1, '#fff');
    px(ctx, 7, oy + 13, 1, 1, '#ffe680');
  }
  if (look.shirtStyle === 'suit') px(ctx, 5, oy + 9, 2, 9, look.shirtAlt);
  if (look.accessory === 'sash')
    for (let i = 0; i < 8; i++) px(ctx, 2 + i, oy + 9 + i, 2, 1, '#fff');
  // head
  const skin = mood === 'angry' ? mixColor(look.skin, '#e0413a', 0.35) : look.skin;
  px(ctx, 3, oy + 2, 6, 7, skin);
  px(ctx, 2, oy + 3, 8, 5, skin);
  if (look.hairStyle === 'afro') {
    px(ctx, 1, oy, 10, 4, hair);
    px(ctx, 0, oy + 1, 12, 3, hair);
  } else if (look.hairStyle !== 'bald') {
    px(ctx, 2, oy + 1, 8, 2, hair);
    if (look.hairStyle === 'long') {
      px(ctx, 1, oy + 2, 2, 8, hair);
      px(ctx, 9, oy + 2, 2, 8, hair);
    }
    if (look.hairStyle === 'bun') px(ctx, 4, oy - 1, 4, 2, hair);
  }
  if (look.hat === 'church' || look.hat === 'cowboy') {
    px(ctx, 0, oy + 2, 12, 1, look.hatColor);
    px(ctx, 2, oy, 8, 2, look.hatColor);
  } else if (
    look.hat === 'trucker' ||
    look.hat === 'cap' ||
    look.hat === 'beanie' ||
    look.hat === 'visor'
  ) {
    px(ctx, 2, oy, 8, 2, look.hatColor);
    px(ctx, 1, oy + 2, 10, 1, shade(look.hatColor, 0.25));
  } else if (look.hat === 'tiara') {
    px(ctx, 4, oy, 4, 1, '#ffd23f');
    px(ctx, 5, oy - 1, 2, 1, '#ff5fa2');
  }
  const eye = look.eyes === 'sunglasses' ? '#0d0d12' : '#1a1410';
  if (look.eyes === 'sunglasses') {
    px(ctx, 3, oy + 4, 6, 1, eye);
  } else if (mood === 'blink') {
    px(ctx, 4, oy + 5, 1, 1, eye);
    px(ctx, 7, oy + 5, 1, 1, eye);
  } else {
    px(ctx, 4, oy + 4, 1, 2, eye);
    px(ctx, 7, oy + 4, 1, 2, eye);
  }
  if (mood === 'talk' || mood === 'eat') px(ctx, 5, oy + 7, 2, 1, '#3a1512');
  else if (mood === 'happy') px(ctx, 5, oy + 7, 2, 1, shade(skin, 0.45));
}

export function walkerSprite(look: CharacterLook, frame: 0 | 1, angry = false): Sprite {
  return spriteFromDraw(`walker:${lookKey(look)}:${frame}:${angry}`, WALKER_W, WALKER_H, (ctx) => {
    if (look.species === 'raccoon') {
      px(ctx, 2, 14, 8, 5, '#8a8f98');
      px(ctx, 8, 12, 3, 3, '#8a8f98');
      px(ctx, 9, 13, 2, 1, '#1e2026');
      px(ctx, 0, 13, 3, 2, '#4a4e57');
      px(ctx, 3 + frame, 19, 1, 2, '#4a4e57');
      px(ctx, 7 - frame, 19, 1, 2, '#4a4e57');
      return;
    }
    if (look.species === 'host') {
      px(ctx, 2, 0, 8, 24, '#0b0b10');
      px(ctx, 4, 2, 4, 5, '#ffd23f');
      return;
    }
    drawMini(ctx, look, angry ? 'angry' : 'normal', 0);
    const pants =
      look.shirtStyle === 'suit'
        ? '#1e1e24'
        : look.shirtStyle === 'scrubs'
          ? look.shirt
          : '#2f3e6b';
    if (frame === 0) {
      px(ctx, 3, 18, 2, 5, pants);
      px(ctx, 7, 18, 2, 5, pants);
      px(ctx, 2, 23, 3, 1, '#1a1a1a');
      px(ctx, 7, 23, 3, 1, '#1a1a1a');
    } else {
      px(ctx, 2, 18, 2, 5, pants);
      px(ctx, 8, 18, 2, 5, pants);
      px(ctx, 1, 23, 3, 1, '#1a1a1a');
      px(ctx, 8, 23, 3, 1, '#1a1a1a');
    }
  });
}

/** Big-ish standing brawler for the fight (with a jab pose). */
export function fighterSprite(look: CharacterLook, pose: 0 | 1 | 2): Sprite {
  return spriteFromDraw(`fighter:${lookKey(look)}:${pose}`, 16, 26, (ctx) => {
    ctx.save();
    ctx.translate(2, 1);
    drawMini(ctx, look, pose === 2 ? 'angry' : 'talk', 0);
    ctx.restore();
    px(ctx, 5, 19, 2, 6, '#2f3e6b');
    px(ctx, 9, 19, 2, 6, '#2f3e6b');
    if (pose === 1) {
      // jab!
      px(ctx, 12, 11, 4, 2, look.shirt);
      px(ctx, 15, 10, 1, 3, look.skin);
    } else if (pose === 2) {
      px(ctx, 0, 6, 2, 5, look.shirt);
      px(ctx, 14, 6, 2, 5, look.shirt);
      px(ctx, 0, 5, 2, 1, look.skin);
      px(ctx, 14, 5, 2, 1, look.skin);
    }
  });
}

/** Miss Dottie, the server who has seen it all. */
export function dottieSprite(mood: Mood): Sprite {
  const look: CharacterLook = {
    species: 'human',
    skin: '#c68a5e',
    hair: '#d8d8d8',
    hairStyle: 'bun',
    hat: 'visor',
    hatColor: '#1e1e24',
    eyes: 'glasses',
    facialHair: 'none',
    shirt: '#f4c20d',
    shirtAlt: '#1e1e24',
    shirtStyle: 'polo',
    accessory: 'nametag',
  };
  return seatedSprite(look, mood);
}
